/**
 * Página de Aceitação de Convite - Redesign Premium
 * Layout split-screen moderno igual ao Auth.tsx
 * Detecção automática de conta existente (sem tabs confusos)
 * Terminologia amigável: "Juntar-se à equipe" em vez de "Criar conta"
 * Redirecionamento corrigido para /tenant após aceitar convite
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useSystemSettings } from '@/hooks/useSystemSettings';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';

import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { translateAuthError } from '@/lib/auth-utils';
import { 
  Building2, 
  Mail, 
  User, 
  Clock, 
  AlertCircle, 
  CheckCircle2, 
  Loader2,
  Shield,
  Users,
  Headset,
  Quote,
  UserPlus,
  LogIn,
  ArrowRight,
  Check,
  X
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { LucideIcon } from 'lucide-react';
import Lottie from 'lottie-react';
import chatSupportAnimation from '@/assets/animations/chat-support.json';

// Interface para dados do convite
interface InviteInfo {
  email: string;
  role: string;
  status: string;
  expires_at: string;
  tenant: {
    id: string;
    name: string;
    slug: string;
    logo_url: string | null;
  };
  invited_by: {
    full_name: string | null;
    email: string;
  };
}

// Mapeamento de roles para labels amigáveis com ícones e descrições
interface RoleConfig {
  label: string;
  icon: LucideIcon;
  description: string;
  color: string;
}

const roleLabels: Record<string, RoleConfig> = {
  'tenant_admin': { 
    label: 'Administrador', 
    icon: Shield, 
    description: 'Acesso completo à organização',
    color: 'bg-amber-500/10 text-amber-600 border-amber-500/20'
  },
  'tenant_supervisor': { 
    label: 'Supervisor', 
    icon: Users, 
    description: 'Gerenciar equipe e atendimentos',
    color: 'bg-blue-500/10 text-blue-600 border-blue-500/20'
  },
  'agent': { 
    label: 'Agente de Atendimento', 
    icon: Headset, 
    description: 'Atender clientes via chat',
    color: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'
  },
  'user': { 
    label: 'Membro', 
    icon: User, 
    description: 'Acesso básico ao sistema',
    color: 'bg-slate-500/10 text-slate-600 border-slate-500/20'
  }
};

/**
 * Versículos da Bíblia - Nova Tradução na Linguagem de Hoje (NTLH)
 * Rotacionados aleatoriamente a cada 2 minutos
 */
const versiculosBiblia = [
  { texto: "Confie no SENHOR de todo o coração e não se apoie na sua própria inteligência.", referencia: "Provérbios 3:5" },
  { texto: "Eu sei muito bem os planos que tenho para vocês, planos de paz e não de desgraça, planos para dar a vocês um futuro e uma esperança.", referencia: "Jeremias 29:11" },
  { texto: "O SENHOR é o meu pastor: nada me faltará.", referencia: "Salmos 23:1" },
  { texto: "Tudo posso naquele que me fortalece.", referencia: "Filipenses 4:13" },
  { texto: "Não tenha medo, pois eu estou com você; não fique aflito, pois eu sou o seu Deus. Eu dou forças a você e o ajudo.", referencia: "Isaías 41:10" },
  { texto: "Entreguem todas as suas preocupações a Deus, pois ele cuida de vocês.", referencia: "1 Pedro 5:7" },
];

// Obter iniciais do nome para avatar
const getInitials = (name: string | null | undefined): string => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Obter configuração do role (com fallback)
const getRoleConfig = (role: string): RoleConfig => {
  return roleLabels[role] || {
    label: role,
    icon: User,
    description: 'Membro da equipe',
    color: 'bg-slate-500/10 text-slate-600 border-slate-500/20'
  };
};

const AcceptInvite = () => {
  const { t } = useTranslation('auth');
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, session, loading: authLoading, signIn, signUp, refreshTenants } = useAuth();
  const { branding } = useSystemSettings();
  
  // Estados principais
  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Estados do formulário de autenticação
  // 'login' = tem conta, 'signup' = nova conta (padrão para convites)
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [authLoading2, setAuthLoading2] = useState(false);
  
  // Estado para tela de confirmação de email após signup
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);

  // Estado para validação de senha em tempo real (mínimo 8 caracteres)
  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,    // ≥ 8 caracteres
    hasLower: false,     // letra minúscula
    hasUpper: false,     // letra maiúscula  
    hasDigit: false,     // número
    hasSymbol: false     // símbolo
  });

  // Estado para versículo atual (randômico)
  const [versiculoIndex, setVersiculoIndex] = useState(() => 
    Math.floor(Math.random() * versiculosBiblia.length)
  );

  // Rotaciona versículos a cada 2 minutos
  useEffect(() => {
    const interval = setInterval(() => {
      setVersiculoIndex(prev => {
        let newIndex;
        do {
          newIndex = Math.floor(Math.random() * versiculosBiblia.length);
        } while (newIndex === prev && versiculosBiblia.length > 1);
        return newIndex;
      });
    }, 120000); // 2 minutos

    return () => clearInterval(interval);
  }, []);

  /**
   * Processa tokens de autenticação do hash da URL
   * Quando usuário confirma email e é redirecionado de volta para /invite/:token,
   * o Supabase inclui access_token e refresh_token no hash da URL.
   * Este useEffect estabelece a sessão automaticamente para permitir auto-aceite.
   */
  useEffect(() => {
    const processHashTokens = async () => {
      const hash = window.location.hash;
      
      // Verifica se há tokens no hash da URL
      if (!hash || !hash.includes('access_token')) {
        return;
      }
      
      const hashParams = new URLSearchParams(hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      
      if (!accessToken || !refreshToken) {
        return;
      }
      
      console.log('[AcceptInvite] Tokens detectados no hash, estabelecendo sessão...');
      
      try {
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        
        if (error) {
          console.error('[AcceptInvite] Erro ao estabelecer sessão:', error.message);
          return;
        }
        
        // Limpa hash para evitar reprocessamento e manter URL limpa
        window.history.replaceState(
          null, 
          '', 
          window.location.pathname + window.location.search
        );
        
        console.log('[AcceptInvite] Sessão estabelecida com sucesso, auto-aceite será acionado');
      } catch (err) {
        console.error('[AcceptInvite] Erro ao processar tokens:', err);
      }
    };
    
    processHashTokens();
  }, []);

  // Atualiza validação de senha conforme usuário digita
  useEffect(() => {
    setPasswordValidation({
      minLength: password.length >= 8,
      hasLower: /[a-z]/.test(password),
      hasUpper: /[A-Z]/.test(password),
      hasDigit: /\d/.test(password),
      hasSymbol: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    });
  }, [password]);

  // Verifica se senha atende todos os requisitos (apenas no modo signup)
  const isPasswordValid = authMode === 'login' || (
    passwordValidation.minLength &&
    passwordValidation.hasLower &&
    passwordValidation.hasUpper &&
    passwordValidation.hasDigit &&
    passwordValidation.hasSymbol
  );

  const versiculoAtual = versiculosBiblia[versiculoIndex];

  // Carregar dados do convite
  useEffect(() => {
    if (token) {
      fetchInvite();
    }
  }, [token]);

  // Auto-aceitar se usuário está logado e email corresponde
  useEffect(() => {
    if (!authLoading && user && invite && invite.status === 'pending') {
      if (user.email?.toLowerCase() === invite.email.toLowerCase()) {
        acceptInvite();
      }
    }
  }, [user, authLoading, invite]);

  /**
   * Busca os dados do convite e verifica se o email já tem conta
   */
  const fetchInvite = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-invite?token=${token}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await response.json();

      if (!response.ok) {
        setError(result.error || 'Convite não encontrado');
        return;
      }

      setInvite(result);
      setEmail(result.email);
      // authMode já é 'signup' por padrão - usuário pode alternar manualmente se já tiver conta
    } catch (err) {
      console.error('[AcceptInvite] Erro ao carregar convite:', err);
      setError('Erro ao carregar convite');
    } finally {
      setLoading(false);
    }
  };

  // Função checkExistingAccount removida - verificação automática não é confiável
  // O usuário pode alternar manualmente entre login/signup usando os botões

  /**
   * Aceita o convite e redireciona para o tenant
   * Usa refreshTenants() e window.location.href para garantir reload completo
   */
  const acceptInvite = async () => {
    if (!session?.access_token || !token) return;

    setAccepting(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/accept-invite`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ token }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        toast.error(result.error || 'Erro ao aceitar convite');
        return;
      }

      toast.success('Você agora faz parte da equipe!');
      
      // CORREÇÃO: Atualizar tenants antes de redirecionar
      // Isso garante que o currentTenant seja populado corretamente
      await refreshTenants();
      
      // Usar window.location para garantir reload completo do AuthProvider
      // Evita que o usuário seja redirecionado para /onboarding por engano
      // Redireciona agentes para /tenant/chat, outros roles para /tenant
      const targetRoute = invite?.role === 'agent' ? '/tenant/chat' : '/tenant';
      window.location.href = targetRoute;
    } catch (err) {
      console.error('[AcceptInvite] Erro ao aceitar convite:', err);
      toast.error('Erro ao aceitar convite');
    } finally {
      setAccepting(false);
    }
  };

  /**
   * Processa autenticação (login ou signup)
   * Para signup, usa emailRedirectTo apontando de volta para o convite
   * para que após confirmação do email o usuário retorne automaticamente
   */
  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading2(true);

    try {
      if (authMode === 'login') {
        // Login - usa o signIn do hook, auto-aceita via useEffect
        const { error } = await signIn(email, password);
        if (error) {
          const translatedError = translateAuthError(error.message, t);
          toast.error(translatedError);
        }
        // Se login bem sucedido, o useEffect vai auto-aceitar o convite
      } else {
        // Signup - redireciona de volta para /invite/:token após confirmação de email
        // Isso permite que o auto-aceite funcione automaticamente quando usuário retornar
        const successUrl = `${window.location.origin}/invite/${token}`;
        
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: successUrl, // Redireciona para /auth após confirmação de email
            data: {
              full_name: fullName,
            },
          },
        });

        if (error) {
          const translatedError = translateAuthError(error.message, t);
          toast.error(translatedError);
        } else if (data?.user?.identities?.length === 0) {
          // Email já existe no sistema - sugerir login
          toast.error('Este email já possui uma conta. Use "Já tem conta? Entre aqui" para acessar.');
          setAuthMode('login');
        } else {
          // Signup bem-sucedido - mostrar tela de confirmação de email
          console.log('[AcceptInvite] Signup realizado, aguardando confirmação de email');
          setShowEmailConfirmation(true);
        }
      }
    } catch (err) {
      console.error('[AcceptInvite] Erro de autenticação:', err);
      toast.error('Erro de autenticação');
    } finally {
      setAuthLoading2(false);
    }
  };

  // ================== RENDERIZAÇÃO DE ESTADOS ==================

  // Estado: Aguardando confirmação de email após signup
  if (showEmailConfirmation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md text-center">
          {/* Ícone de email */}
          <div className="mx-auto mb-6 p-4 rounded-full bg-primary/10 w-fit">
            <Mail className="h-12 w-12 text-primary" />
          </div>
          
          {/* Título */}
          <h2 className="text-2xl font-bold text-foreground mb-4">
            Verifique seu Email
          </h2>
          
          {/* Descrição */}
          <p className="text-muted-foreground mb-2">
            Enviamos um link de confirmação para:
          </p>
          <p className="font-semibold text-foreground mb-6 text-lg">
            {email}
          </p>
          
          {/* Instrução de redirecionamento */}
          <div className="bg-muted/50 rounded-xl p-4 mb-6">
            <p className="text-sm text-muted-foreground">
              Após confirmar seu email, você será redirecionado automaticamente para entrar na equipe de{' '}
              <strong className="text-foreground">{invite?.tenant.name}</strong>.
            </p>
          </div>
          
          {/* Dica */}
          <p className="text-xs text-muted-foreground">
            Não recebeu? Verifique sua pasta de spam ou lixo eletrônico.
          </p>
          
          {/* Botão para tentar novamente */}
          <Button 
            variant="outline" 
            className="mt-6"
            onClick={() => setShowEmailConfirmation(false)}
          >
            Voltar e tentar novamente
          </Button>
        </div>
      </div>
    );
  }

  // Loading state - Skeleton premium
  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-6 p-8">
          <Skeleton className="h-16 w-16 rounded-full" />
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
          <div className="flex gap-2 mt-4">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <span className="text-muted-foreground">Carregando convite...</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state - Card ilustrado
  if (error || !invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto mb-6 p-4 rounded-full bg-destructive/10 w-fit">
            <AlertCircle className="h-12 w-12 text-destructive" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-3">
            Convite Inválido
          </h2>
          <p className="text-muted-foreground mb-8">
            {error || 'Este convite não existe ou expirou.'}
          </p>
          <Button variant="outline" onClick={() => navigate('/')}>
            Voltar ao Início
          </Button>
        </div>
      </div>
    );
  }

  // Invite not pending - Already accepted, expired, or cancelled
  if (invite.status !== 'pending') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto mb-6 p-4 rounded-full bg-muted w-fit">
            {invite.status === 'accepted' ? (
              <CheckCircle2 className="h-12 w-12 text-primary" />
            ) : (
              <Clock className="h-12 w-12 text-muted-foreground" />
            )}
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-3">
            {invite.status === 'accepted' ? 'Convite Já Aceito' : 
             invite.status === 'expired' ? 'Convite Expirado' : 'Convite Cancelado'}
          </h2>
          <p className="text-muted-foreground mb-8">
            {invite.status === 'accepted' 
              ? 'Este convite já foi aceito anteriormente.' 
              : invite.status === 'expired'
              ? 'Este convite expirou. Solicite um novo convite ao administrador.'
              : 'Este convite foi cancelado.'}
          </p>
          <div className="flex justify-center gap-4">
            <Button variant="outline" onClick={() => navigate('/')}>
              Voltar ao Início
            </Button>
            {invite.status === 'accepted' && (
              <Button onClick={() => navigate('/tenant')}>
                Ir para o Painel
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // User logged in with different email
  if (user && user.email?.toLowerCase() !== invite.email.toLowerCase()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto mb-6 p-4 rounded-full bg-orange-100 dark:bg-orange-900/30 w-fit">
            <AlertCircle className="h-12 w-12 text-orange-500" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-3">
            Email Diferente
          </h2>
          <p className="text-muted-foreground mb-4">
            Este convite foi enviado para:
          </p>
          <p className="font-semibold text-foreground mb-4">
            {invite.email}
          </p>
          <p className="text-muted-foreground mb-8">
            Mas você está logado como <strong>{user.email}</strong>.
          </p>
          <Button onClick={() => supabase.auth.signOut()}>
            Sair e usar outra conta
          </Button>
        </div>
      </div>
    );
  }

  // Accepting state - Animation
  if (user && accepting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-6 text-center">
          <div className="relative">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Entrando na equipe...
            </h2>
            <p className="text-muted-foreground">
              Preparando seu acesso a {invite.tenant.name}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Obter configuração do role
  const roleConfig = getRoleConfig(invite.role);
  const RoleIcon = roleConfig.icon;

  // ================== LAYOUT PRINCIPAL - SPLIT SCREEN ==================
  return (
    <div className="min-h-screen flex">
      {/* ======== LADO ESQUERDO - BRANDING (apenas desktop) ======== */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#0a2540] via-[#1a3a5c] to-[#0d4a6b] text-white flex-col justify-between p-12 relative overflow-hidden">
        {/* Background pattern sutil - Padrão de pontos */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.3) 2px, transparent 2px)`,
            backgroundSize: '50px 50px'
          }} />
        </div>
        
        {/* Elementos decorativos turquesa */}
        <div className="absolute top-20 left-20 w-32 h-32 bg-[#2dd4bf]/20 rounded-full blur-xl animate-pulse" />
        <div className="absolute bottom-40 right-16 w-24 h-24 bg-[#2dd4bf]/15 rounded-full blur-xl animate-pulse" />
        <div className="absolute top-1/2 right-1/4 w-20 h-20 bg-[#2dd4bf]/10 rounded-full blur-xl animate-pulse" />

        {/* Logo do sistema */}
        <div className="relative z-10">
          {branding.logo_url ? (
            <img 
              src={branding.logo_url} 
              alt={branding.app_name} 
              className="h-10 brightness-0 invert opacity-90" 
            />
          ) : (
            <div className="flex items-center gap-2">
              <Building2 className="h-8 w-8" />
              <span className="text-xl font-semibold">{branding.app_name}</span>
            </div>
          )}
        </div>

        {/* Área central - Informações do convite + Animação */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center">
          {/* Card do tenant */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8 max-w-sm w-full">
            {/* Avatar/Logo do tenant */}
            <div className="flex items-center gap-4 mb-4">
              <Avatar className="h-14 w-14 border-2 border-white/20">
                {invite.tenant.logo_url ? (
                  <AvatarImage src={invite.tenant.logo_url} alt={invite.tenant.name} />
                ) : null}
                <AvatarFallback className="bg-white/20 text-white text-lg font-semibold">
                  {getInitials(invite.tenant.name)}
                </AvatarFallback>
              </Avatar>
              <div>
                <h3 className="text-lg font-semibold">{invite.tenant.name}</h3>
                <p className="text-white/70 text-sm">Organização</p>
              </div>
            </div>

            {/* Quem convidou */}
            <div className="flex items-center gap-3 py-3 border-t border-white/10">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-white/20 text-white text-xs">
                  {getInitials(invite.invited_by.full_name || invite.invited_by.email)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-white/90 text-sm truncate">
                  {invite.invited_by.full_name || invite.invited_by.email.split('@')[0]}
                </p>
                <p className="text-white/60 text-xs">convidou você</p>
              </div>
            </div>
          </div>

          {/* Animação Lottie */}
          <div className="w-64 h-64 opacity-80">
            <Lottie 
              animationData={chatSupportAnimation} 
              loop={true}
              className="w-full h-full"
            />
          </div>
        </div>

        {/* Versículo bíblico */}
        <div className="relative z-10">
          <div className="flex items-start gap-3 opacity-80">
            <Quote className="h-5 w-5 mt-1 flex-shrink-0 opacity-60" />
            <div>
              <p className="text-sm leading-relaxed italic">
                "{versiculoAtual.texto}"
              </p>
              <p className="text-xs opacity-60 mt-2">— {versiculoAtual.referencia}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ======== LADO DIREITO - FORMULÁRIO ======== */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center items-center p-6 sm:p-8 lg:p-12 bg-background">
        <div className="w-full max-w-md">
          {/* Logo mobile */}
          <div className="lg:hidden flex justify-center mb-8">
            {branding.logo_url ? (
              <img 
                src={branding.logo_url} 
                alt={branding.app_name} 
                className="h-10 object-contain" 
              />
            ) : (
              <div className="flex items-center gap-2">
                <Building2 className="h-8 w-8 text-primary" />
                <span className="text-xl font-semibold text-foreground">{branding.app_name}</span>
              </div>
            )}
          </div>

          {/* Título principal */}
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
              Junte-se à equipe
            </h1>
            <p className="text-muted-foreground">
              Você foi convidado para fazer parte de <strong className="text-foreground">{invite.tenant.name}</strong>
            </p>
          </div>

          {/* Card de informações do convite (mobile + desktop) */}
          <div className="bg-muted/50 rounded-xl p-4 mb-6">
            {/* Role badge */}
            <div className="flex items-center gap-3 mb-3">
              <div className={`p-2 rounded-lg ${roleConfig.color} border`}>
                <RoleIcon className="h-5 w-5" />
              </div>
              <div>
                <p className="font-medium text-foreground">{roleConfig.label}</p>
                <p className="text-xs text-muted-foreground">{roleConfig.description}</p>
              </div>
            </div>

            {/* Email e expiração */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-muted-foreground pt-3 border-t border-border/50">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                <span className="truncate">{invite.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>Expira {new Date(invite.expires_at).toLocaleDateString('pt-BR')}</span>
              </div>
            </div>
          </div>

          {/* Formulário de autenticação */}
          <form onSubmit={handleAuth} className="space-y-5">
            {/* Mensagem contextual baseada no modo */}
            <div className="text-center py-3 rounded-lg bg-primary/5 border border-primary/10">
              {authMode === 'login' ? (
                <div className="flex items-center justify-center gap-2">
                  <LogIn className="h-4 w-4 text-primary" />
                  <span className="text-sm text-foreground">Bem-vindo de volta! Entre com sua senha</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <UserPlus className="h-4 w-4 text-primary" />
                  <span className="text-sm text-foreground">Complete seu cadastro para entrar</span>
                </div>
              )}
            </div>

            {/* Campo Nome (apenas para signup) */}
            {authMode === 'signup' && (
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-foreground">Nome Completo</Label>
                <Input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Seu nome completo"
                  required={authMode === 'signup'}
                  className="h-12 rounded-xl"
                />
              </div>
            )}

            {/* Campo Email (sempre disabled) */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                disabled
                className="h-12 rounded-xl bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                Email vinculado ao convite
              </p>
            </div>

            {/* Campo Senha */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground">Senha</Label>
              <PasswordInput
                id="password"
                showLockIcon={false}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={authMode === 'login' ? 'Digite sua senha' : 'Crie uma senha segura'}
                required
                minLength={8}
                autoComplete={authMode === 'login' ? 'current-password' : 'new-password'}
                className="h-12 rounded-xl"
              />

              
              {/* Validação visual de requisitos (apenas no modo signup e quando há senha) */}
              {authMode === 'signup' && password.length > 0 && (
                <div className="grid grid-cols-2 gap-1 pt-1 text-xs">
                  <div className={cn("flex items-center gap-1", 
                    passwordValidation.minLength ? "text-emerald-600" : "text-muted-foreground")}>
                    {passwordValidation.minLength ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                    Mínimo 8 caracteres
                  </div>
                  <div className={cn("flex items-center gap-1",
                    passwordValidation.hasLower ? "text-emerald-600" : "text-muted-foreground")}>
                    {passwordValidation.hasLower ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                    Letra minúscula
                  </div>
                  <div className={cn("flex items-center gap-1",
                    passwordValidation.hasUpper ? "text-emerald-600" : "text-muted-foreground")}>
                    {passwordValidation.hasUpper ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                    Letra maiúscula
                  </div>
                  <div className={cn("flex items-center gap-1",
                    passwordValidation.hasDigit ? "text-emerald-600" : "text-muted-foreground")}>
                    {passwordValidation.hasDigit ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                    Número
                  </div>
                  <div className={cn("flex items-center gap-1 col-span-2",
                    passwordValidation.hasSymbol ? "text-emerald-600" : "text-muted-foreground")}>
                    {passwordValidation.hasSymbol ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                    Símbolo (!@#$%...)
                  </div>
                </div>
              )}
            </div>

            {/* Botão de submit - Mesmo estilo do Auth.tsx */}
            <Button 
              type="submit" 
              className="w-full h-12 rounded-xl text-base font-medium bg-gradient-to-r from-[#0a2540] to-[#1a3a5c] hover:from-[#0d3b66] hover:to-[#245f8a] text-white shadow-lg shadow-[#0a2540]/30 transition-all duration-300 hover:shadow-xl hover:shadow-[#0a2540]/40 hover:scale-[1.02] active:scale-[0.98]"
              disabled={authLoading2 || !isPasswordValid}
            >
              {authLoading2 ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Processando...
                </>
              ) : (
                <>
                  {authMode === 'login' ? 'Acessar e Juntar-me' : 'Cadastrar e Juntar-me'}
                  <ArrowRight className="h-5 w-5 ml-2" />
                </>
              )}
            </Button>

            {/* Link para trocar de modo manualmente */}
            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => setAuthMode(authMode === 'login' ? 'signup' : 'login')}
                className="text-sm text-primary hover:underline"
              >
                {authMode === 'login' 
                  ? 'Não tem conta? Cadastre-se' 
                  : 'Já tem conta? Entre aqui'}
              </button>
            </div>
          </form>

          {/* Info mobile - Quem convidou */}
          <div className="lg:hidden mt-8 pt-6 border-t border-border">
            <div className="flex items-center justify-center gap-3 text-sm text-muted-foreground">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-primary/10 text-primary text-xs">
                  {getInitials(invite.invited_by.full_name || invite.invited_by.email)}
                </AvatarFallback>
              </Avatar>
              <span>
                Convite de {invite.invited_by.full_name || invite.invited_by.email.split('@')[0]}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
